import { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import FileValidationMain from "../components/batch/FileValidationMain";
import * as api from "api/endpoints/fileValidation";
import { loadingPage, selectUser } from "store/slices/app";
import { genarate_UUID } from "utils/genarateUUID";
import downloadByUrl from "utils/downloadByUrl";

let initialFieldData = [];

function setInitialFieldData(data) {
  initialFieldData = JSON.parse(JSON.stringify(data || []));
}

export default function TraceSearchPage() {
  const [loading, setLoading] = useState(false);
  const [showFileName, setShowFileName] = useState(false);
  const [disableFileUpload, setDisableFileUpload] = useState(false);
  const [fieldMappingRows, setFieldMappingRows] = useState([]);
  const [fileId, setFileId] = useState(null);
  const [mappedData, setMappedData] = useState({ rows: null, isError: false, msg: null });
  const [hasHeaderRow, setHasHeaderRow] = useState(false);
  const [isHeaderChanged, setIsHeaderChanged] = useState(false);
  const [prevValue, setPrevValue] = useState(false);
  const [fileName, setFileName] = useState();
  const [testMatchData, setTestMatchData] = useState(null);
  const [intervalId, setIntervalId] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [testMatchLoading, setTestMatchLoading] = useState(false);
  const [mappedDataLoading, setMappedDataLoading] = useState(false);
  const [fileLoading, setFileLoading] = useState(false);
  const [showFileAlert, setShowFileAlert] = useState({ type: "", message: "" });
  const [showAlert, setShowAlert] = useState({ type: "", message: "" });
  const [showRunTestError, setShowRunTestError] = useState(false);
  const [abortController, setAbortController] = useState(null);

  const userData = useSelector(selectUser);
  const epId = userData.selectedPartner.id;
  const dispatch = useDispatch();
  const prevSelectedPartnerIdRef = useRef(userData.selectedPartner.id);
  const checkForFileRemove = prevSelectedPartnerIdRef.current !== userData.selectedPartner.id;

  const fileTemplateURL = `${process.env["REACT_APP_PUBLIC_RESOURCE_URL"]}/enterprise-portal-media-files/sample_test_match_file.csv`;

  useEffect(() => {
    if (userData.environment === "production") {
      getMappingData(epId);
      setShowFileName(false);
      setDisableFileUpload(false);
      setMappedData({
        rows: null,
        isError: false,
        msg: null
      });
      setShowFileAlert(false);
      setShowAlert(false);
      setTestMatchData(null);
    }
  }, [epId, userData.environment]);

  useEffect(() => {
    setFileId(localStorage.getItem("fileId"));
    setFileName(localStorage.getItem("fileName"));
    setSessionId(localStorage.getItem("sessionId"));
    if (fileName) {
      setShowFileName(true);
      setDisableFileUpload(true);
    }
  }, [epId, fileName]);

  useEffect(() => {
    if (checkForFileRemove) {
      localStorage.removeItem("fileId");
      localStorage.removeItem("fileName");
      localStorage.removeItem("sessionId");
      setFileId(null);
      setFileName(null);
      setSessionId(null);
      setShowFileName(false);
      setDisableFileUpload(false);
    }
  }, [checkForFileRemove, userData.selectedPartner.id]);

  useEffect(() => {
    return function () {
      if (intervalId) {
        clearInterval(intervalId);
        setTestMatchLoading(false);
      }
    };
  }, [intervalId]);

  async function getMappingData(epId) {
    const mappingData = await api.getFieldMapping(epId);
    setInitialFieldData(mappingData.details.fieldData);
    setFieldMappingRows(mappingData.details.fieldData || []);
    setHasHeaderRow(mappingData.details.containsHeader);
    setPrevValue(mappingData.details.containsHeader);
  }

  function handleFieldChange(row, value) {
    const newValue = value.trim() === "" ? null : value;
    const rows = fieldMappingRows.map((field) =>
      field.fieldName === row.fieldName ? { ...field, fieldOrder: newValue } : field
    );
    setFieldMappingRows(rows);
  }

  function handleFileRemove() {
    if (abortController) {
      abortController.abort();
    }
    localStorage.removeItem("fileId");
    localStorage.removeItem("fileName");
    localStorage.removeItem("sessionId");
    dispatch(loadingPage(false));
    setShowFileAlert(false);
    setFileLoading(false);
    setDisableFileUpload(false);
    setShowFileName(false);
    setMappedData({
      rows: null,
      isError: false,
      msg: null
    });
    setFileId(null);
    setTestMatchData(null);
  }

  function handleDownload() {
    downloadByUrl(fileTemplateURL);
  }

  function handleHeaderRowChange(e) {
    setHasHeaderRow(e.target.checked);
    setPrevValue(hasHeaderRow);
    setIsHeaderChanged(true);
  }

  async function handleSaveFieldMapping() {
    dispatch(loadingPage(true));
    setLoading(true);
    setShowFileAlert(false);
    setShowAlert(false);
    setMappedData(false);
    const payload = {
      details: {
        containsHeader: hasHeaderRow,
        fieldData: fieldMappingRows
      }
    };

    try {
      await api.updateFieldMapping(epId, payload);
      setInitialFieldData(fieldMappingRows);
      setShowAlert({
        type: "success",
        message: "Field Mapping Saved!"
      });
      setIsHeaderChanged(false);
    } catch (error) {
      const msg = error.response?.data?.errors || "Something went wrong";
      setShowAlert({
        type: "error",
        message: msg
      });
    } finally {
      setLoading(false);
      dispatch(loadingPage(false));
    }
  }

  async function handleFileSelect(event) {
    setShowFileAlert(false);
    setShowAlert(false);
    setMappedData(false);
    const sessionId = genarate_UUID();
    const formData = new FormData();
    const files = event.target.files;

    for (let i = 0; i < files.length; i++) {
      if (files[i].size > 20 * 1024 * 1024) {
        event.target.value = "";
        setShowFileAlert({ type: "error", message: "File size should be less than 20MB" });
        return;
      }
    }
    const fileName = files[0]?.name;
    setFileName(fileName);
    setSessionId(sessionId);
    localStorage.setItem("sessionId", sessionId);
    for (let i = 0; i < files.length; i++) {
      formData.append("testFile", files[i]);
      const fileName = files[i]?.name;
      setFileName(fileName);
      localStorage.setItem("fileName", fileName);
    }
    event.target.value = "";
    formData.append("epId", epId);
    formData.append("sessionId ", sessionId);

    const controller = new AbortController();
    const signal = controller.signal;
    setAbortController(controller);

    dispatch(loadingPage(true));
    setFileLoading(true);
    try {
      const response = await api.uploadTestFile(formData, signal.aborted);
      if (signal.aborted) {
        controller.abort();
        return;
      }
      setFileId(response.details.fileName);
      localStorage.setItem("fileId", response.details.fileName);
      setShowFileAlert({ type: "success", message: "Successfully Updated" });
      setShowFileName(true);
      setDisableFileUpload(true);
    } catch (error) {
      setDisableFileUpload(false);
      setShowFileName(false);
      localStorage.removeItem("fileId");
      localStorage.removeItem("fileName");
      localStorage.removeItem("sessionId");
      const msg = error.response?.data?.errors || "Something went wrong";
      setShowFileAlert({ type: "error", message: msg });
    } finally {
      setFileLoading(false);
      dispatch(loadingPage(false));
    }
  }

  async function handleShowMappedData() {
    dispatch(loadingPage(true));
    setMappedDataLoading(true);
    setShowFileAlert(false);
    setShowAlert(false);
    setMappedData(false);
    const data = {
      epId: epId,
      fileId: fileId
    };
    try {
      const response = await api.getMappedData(data);
      setMappedData({
        rows: response.details || [],
        isError: false,
        msg: null
      });
    } catch (error) {
      setMappedData({
        rows: null,
        isError: true,
        msg: error.response
      });
    } finally {
      setMappedDataLoading(false);
      dispatch(loadingPage(false));
    }
  }

  async function handleResetField() {
    const rows = fieldMappingRows.map((field) => {
      const oldField = initialFieldData.find((f) => f.fieldName === field.fieldName);
      return { ...field, fieldOrder: oldField.fieldOrder };
    });
    if (isHeaderChanged) {
      setHasHeaderRow(prevValue);
    }
    setFieldMappingRows(rows);
  }

  async function handleClearAll() {
    const rows = fieldMappingRows.map((field) => {
      return { ...field, fieldOrder: null };
    });
    setFieldMappingRows(rows);
  }

  async function handleRunMatch() {
    dispatch(loadingPage(true));
    setTestMatchLoading(true);
    setShowFileAlert(false);
    setShowAlert(false);
    try {
      await api.runTestMatch({
        epId: epId,
        fileId: fileId,
        sessionId: sessionId
      });

      const intervalId = setInterval(async function () {
        const data = await api.getTestMatch({
          epId,
          fileId,
          sessionId
        });

        setIntervalId(intervalId);
        setTestMatchData(data);
        setShowRunTestError(data?.testSummary?.errorCount || data?.status === "Error");

        if (data.status === "Completed" || data.status === "Error") {
          clearInterval(intervalId);
          setTestMatchLoading(false);
          dispatch(loadingPage(false));
        }
      }, 3000);
    } catch (error) {
      clearInterval(intervalId);
      setTestMatchLoading(false);
    } finally {
      localStorage.removeItem("fileId");
      localStorage.removeItem("fileName");
      localStorage.removeItem("sessionId");
    }
  }
  async function handleAlertClose(name) {
    switch (name) {
      case "smd":
        setMappedData({
          rows: null,
          isError: false
        });
        break;
      case "file":
        setShowFileAlert({
          type: "",
          message: ""
        });
        break;
      case "fm":
        setShowAlert(false);
        break;
      case "rtm":
        setShowRunTestError(false);
        setTestMatchData(null);
        break;

      default:
        break;
    }
  }

  return (
    <FileValidationMain
      loading={loading}
      showFileName={showFileName}
      disableFileUpload={disableFileUpload}
      fieldMappingRows={fieldMappingRows}
      mappedData={mappedData}
      fileName={fileName}
      hasHeaderRow={hasHeaderRow}
      testMatchLoading={testMatchLoading}
      testMatchData={testMatchData}
      loadData={userData.selectedPartner.id}
      showAlert={showAlert}
      showRunTestError={showRunTestError}
      showFileAlert={showFileAlert}
      mappedDataLoading={mappedDataLoading}
      fileLoading={fileLoading}
      onFieldChange={handleFieldChange}
      onUpdate={handleSaveFieldMapping}
      onFileSelect={handleFileSelect}
      onRemove={handleFileRemove}
      onDownload={handleDownload}
      onShowMappedData={handleShowMappedData}
      onHeaderRowChange={handleHeaderRowChange}
      onReset={handleResetField}
      onClearAll={handleClearAll}
      onRunMatch={handleRunMatch}
      onAlertClose={handleAlertClose}
    />
  );
}
